import { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { userCustomSettingsSelector } from 'store/selectors/app-selectors';
import { ComparisonDates } from 'types/properties-data';
import { useDateOptions } from 'components/date-compare-select';
import { DEFAULT_COMPARE_GRANULARITY, DEFAULT_GRANULARITY } from 'shared/constants/dates';
import { DateOption, GranularityType } from 'types/dates-types';

// format: 'Granularity-GranularityYear' || 'Granularity'
export const useDefaultDatesGranularity = () => {
    const userCustomSettings = useSelector(userCustomSettingsSelector);
    return userCustomSettings?.myProperties?.dates?.selectedDateGranularity ?? DEFAULT_GRANULARITY;
};

// format: 'Granularity-GranularityYear' || 'Granularity'
const useDefaultCompareDatesGranularity = () => {
    const userCustomSettings = useSelector(userCustomSettingsSelector);
    return (
        userCustomSettings?.myProperties?.dates?.selectedCompareDateGranularity ??
        DEFAULT_COMPARE_GRANULARITY
    );
};

const findDateOption = (dateOptions: DateOption[] | undefined, granularityKey: GranularityType) => {
    return dateOptions?.find((option) => option.key === granularityKey) || dateOptions?.[0];
};

export const useDefaultComparisonDates = (): ComparisonDates | undefined => {
    const dateOptions = useDateOptions();
    const selectedGranularity = useDefaultDatesGranularity();
    const selectedCompareGranularity = useDefaultCompareDatesGranularity();

    const { defaultDateOption, defaultComparedDate } = useMemo(() => {
        const defaultDateOption = dateOptions.find((option) => option.key === selectedGranularity);
        const compareDateOptions = defaultDateOption?.compareOptions;
        const defaultComparedDate = findDateOption(compareDateOptions, selectedCompareGranularity);

        if (!defaultComparedDate) {
            const defaultOption = dateOptions.find((option) => option.key === DEFAULT_GRANULARITY);
            const defaultCompareDateOptions = defaultOption?.compareOptions;
            const defaultCompareOption = findDateOption(
                defaultCompareDateOptions,
                selectedCompareGranularity,
            );

            return {
                defaultDateOption: defaultOption,
                defaultComparedDate: defaultCompareOption,
            };
        }

        return {
            defaultDateOption,
            defaultComparedDate,
        };
    }, [dateOptions, selectedCompareGranularity, selectedGranularity]);

    if (!defaultDateOption || !defaultComparedDate) {
        return undefined;
    }

    return {
        start_date: defaultDateOption.startDate,
        end_date: defaultDateOption.endDate,
        compared_start_date: defaultComparedDate.startDate,
        compared_end_date: defaultComparedDate.endDate,
    };
};
